import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnInit,
  OnChanges,
  SimpleChanges,
  Injector,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
  NgControl,
  FormsModule,
} from '@angular/forms';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { RadioButtonModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';

export interface RadioOption {
  id: string;
  label: string;
  value: any;
  checked?: boolean;
  disabled?: boolean;
  description?: string;
}

export interface RadioConfig {
  options?: RadioOption[];
  disabled?: boolean;
  labelPosition?: 'before' | 'after';
  size?: 'small' | 'medium' | 'large';
  rounded?: 'small' | 'medium' | 'large' | 'full';
  layout?: 'horizontal' | 'vertical';
  name?: string;
  showClearButton?: boolean;
  cssClass?: string;
  required?: boolean;
  errorMessage?: string;
  showErrors?: boolean;
  hint?: string;
}

@Component({
  selector: 'app-radio',
  standalone: true,
  imports: [CommonModule, FormsModule, RadioButtonModule, ButtonsModule, LabelModule],
  templateUrl: './radio.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RadioComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RadioComponent implements ControlValueAccessor, Validator, OnInit, OnChanges {
  @Input() config: RadioConfig = {};

  @Output() selectionChange = new EventEmitter<RadioOption | null>();
  @Output() valueChange = new EventEmitter<any>();
  @Output() optionClick = new EventEmitter<RadioOption>();

  // Internal properties with default values
  options: RadioOption[] = [];
  disabled: boolean = false;
  labelPosition: 'before' | 'after' = 'after';
  size: 'small' | 'medium' | 'large' = 'medium';
  rounded: 'small' | 'medium' | 'large' | 'full' = 'medium';
  layout: 'horizontal' | 'vertical' = 'vertical';
  name: string = '';
  showClearButton: boolean = false;
  cssClass: string = '';
  required: boolean = false;
  errorMessage: string = '';
  showErrors: boolean = true;
  hint: string = '';

  selectedValue: any = null;

  // Validation properties
  touched: boolean = false;
  errors: ValidationErrors | null = null;
  public ngControl: NgControl | null = null;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private isInternalChange = false;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly injector: Injector
  ) {}

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null);
    this.applyConfig();
    this.initializeSelection();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.applyConfig();
      this.updateSelection();
    }
  }

  private applyConfig(): void {
    this.options = this.config.options || [];
    this.disabled = this.config.disabled ?? false;
    this.labelPosition = this.config.labelPosition || 'after';
    this.size = this.config.size || 'medium';
    this.rounded = this.config.rounded || 'medium';
    this.layout = this.config.layout || 'vertical';
    this.name = this.config.name || `radio-group-${Math.random().toString(36).substr(2, 9)}`;
    this.showClearButton = this.config.showClearButton ?? false;
    this.cssClass = this.config.cssClass || '';
    this.required = this.config.required ?? false;
    this.errorMessage = this.config.errorMessage ?? '';
    this.showErrors = this.config.showErrors ?? true;
    this.hint = this.config.hint ?? '';
  }

  // Validator implementation
  validate(control: AbstractControl): ValidationErrors | null {
    const errors: ValidationErrors = {};

    if (this.required) {
      const value = control.value;
      if (value === null || value === undefined || value === '') {
        errors['required'] = true;
      }
    }

    this.errors = Object.keys(errors).length > 0 ? errors : null;
    return this.errors;
  }

  // Validation display getters
  get showErrorState(): boolean {
    if (!this.showErrors) return false;

    const hasErrors = !!(this.errors || this.ngControl?.control?.errors);
    if (!hasErrors) return false;

    const isTouched =
      this.touched || this.ngControl?.control?.touched || this.ngControl?.control?.dirty;

    return !!isTouched;
  }

  get currentErrorMessage(): string {
    if (!this.showErrorState) return '';

    const errors = this.ngControl?.control?.errors || this.errors;
    if (!errors) return '';

    if (errors['required']) {
      return this.errorMessage || 'Please select an option';
    }

    return this.errorMessage || 'Invalid selection';
  }

  onRadioChange(option: RadioOption): void {
    if (option.disabled || this.disabled) {
      return;
    }

    this.isInternalChange = true;
    this.touched = true;

    // Uncheck all other options
    this.options.forEach((opt) => {
      opt.checked = false;
    });

    // Check the selected option
    option.checked = true;
    this.selectedValue = option.value;

    this.onChange(option.value);
    this.onTouched();

    this.selectionChange.emit(option);
    this.valueChange.emit(option.value);
    this.optionClick.emit(option);

    // Trigger validation
    if (this.ngControl?.control) {
      this.ngControl.control.updateValueAndValidity();
    }

    setTimeout(() => {
      this.isInternalChange = false;
    }, 0);

    this.cdr.markForCheck();
  }

  writeValue(value: any): void {
    if (this.isInternalChange) {
      return;
    }

    if (value !== undefined && value !== null && value !== '') {
      this.selectedValue = value;
      this.options.forEach((option) => {
        option.checked = option.value === value;
      });
    } else {
      this.selectedValue = null;
      this.options.forEach((option) => {
        option.checked = false;
      });
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  clearSelection(): void {
    this.touched = true;
    this.options.forEach((opt) => {
      opt.checked = false;
    });
    this.selectedValue = null;

    this.selectionChange.emit(null);
    this.valueChange.emit(null);
    this.onChange(null);
    this.onTouched();

    // Trigger validation
    if (this.ngControl?.control) {
      this.ngControl.control.updateValueAndValidity();
    }

    this.cdr.markForCheck();
  }

  hasSelection(): boolean {
    return this.options.some((opt) => opt.checked);
  }

  getSelectedOption(): RadioOption | null {
    return this.options.find((opt) => opt.checked) || null;
  }

  getSelectedValue(): any {
    const selected = this.getSelectedOption();
    return selected ? selected.value : null;
  }

  setSelectedValue(value: any): void {
    this.options.forEach((option) => {
      option.checked = option.value === value;
    });
    this.selectedValue = value;
    this.cdr.markForCheck();
  }

  private initializeSelection(): void {
    if (this.selectedValue !== null && !this.hasSelection()) {
      const option = this.options.find((opt) => opt.value === this.selectedValue);
      if (option) {
        option.checked = true;
      }
    }
  }

  private updateSelection(): void {
    const checkedOptions = this.options.filter((opt) => opt.checked);
    if (checkedOptions.length > 1) {
      this.options.forEach((opt, index) => {
        if (index > 0 && opt.checked) {
          opt.checked = false;
        }
      });
    }

    const selectedOption = this.getSelectedOption();
    this.selectedValue = selectedOption ? selectedOption.value : null;
  }

  isChecked(option: RadioOption): boolean {
    return option.checked === true;
  }

  isDisabled(option: RadioOption): boolean {
    return option.disabled === true || this.disabled;
  }

  trackByOption(index: number, option: RadioOption): string {
    return option.id;
  }
}
